/**
 * Where each antenna port physically sits in a 32-port (20 ft) box.
 *
 * SOURCE: the SB2204A000 engineering drawing, "SCU and Reader Connections"
 * (`docs/nabors-box-heat-mapping/32-ant-blue-and-orange-shelf heat map.pdf`),
 * read directly. All 32 ports on that drawing agree with the `vgroups` /
 * `hgroups` tables in the Nabors reference heat-map script, so two independent
 * sources describe the same box. Write-up: `docs/antenna-geometry.md`.
 *
 * THE SHAPE, which is not what a flat 1..32 grid suggests:
 *
 *   Each side wall carries THREE BAYS of shelving, four antennas high. Each
 *   bay also has one flat plate mounted off the racking near its uprights. Two
 *   more plates sit in the centre channel, on the ceiling, at the two ends.
 *
 *     Right Side (2-16)   bay 1: 5,4,3,2      + plate 6
 *                         bay 2: 8,9,10,11    + plate 7
 *                         bay 3: 15,14,13,12  + plate 16
 *     Left Side (17-31)   bay 1: 18,19,20,21  + plate 17
 *                         bay 2: 25,24,23,22  + plate 26
 *                         bay 3: 28,29,30,31  + plate 27
 *     Centre ceiling      1 (door end), 32 (far end)
 *
 * Each bay reads top-to-bottom in the OPPOSITE direction to its neighbours --
 * 5 down to 2, then 8 down to 11, then 15 down to 12. That is the cabling
 * serpentining, and it is why consecutive port numbers are not adjacent and a
 * 1..32 grid misplaces half of them.
 *
 * Antenna 1 is on the CEILING at the MAIN ENTRY DOOR end (Jason 2026-09-17),
 * and the drawing's door arrow points at the same end as bay 1 on both walls.
 * That fixes the orientation, so "left" and "right" here mean what the drawing
 * means by them.
 */

/**
 * Shelf configuration. Both documented builds -- "black shelves" and "blue and
 * orange shelf" -- carry the SAME antenna numbering: the two reference
 * drawings differ in the shelving, not in the port layout, and the SB071 hand
 * sketch (black shelves) shows the same bays, plates and serpentine as the
 * engineering drawing. So shelf type does not change where the antennas are,
 * and this one drawing serves every 32-port box.
 *
 * `mttr.smartbox_antenna_layout` still records the type -- it is worth
 * knowing, it is what the photo classifier writes, and it is where a genuinely
 * different build would be told apart if one turns up.
 */
export const KNOWN_LAYOUTS = [
  "reference-32",
  "black-shelves",
  "blue-orange-shelf",
  "txam-sb093",
] as const;
export type LayoutKey = (typeof KNOWN_LAYOUTS)[number];

/** Which surface an antenna is mounted on. */
export type Surface = "left" | "right" | "ceiling";

export interface PlacedAntenna {
  port: number;
  surface: Surface;
  /** Position along the length of the box, increasing away from the door.
   *  Bay index on `reference-32`; feet from the door wall on `txam-sb093`. */
  slot: number;
  /** Height. Shelf level 0-4 on `reference-32`; feet off the floor on
   *  `txam-sb093`. Only compare these within one layout. */
  level: number;
  x: number;
  y: number;
  /** Printed panel label a tech reads off the antenna, e.g. "1-14". Surveyed
   *  layouts only. */
  label?: string;
  /** EPC of the Rebound reference sticker fixed to this antenna's face.
   *  Surveyed layouts only; null where the sticker is dead or unrecorded. */
  sticker?: string | null;
  /** Position name from the survey, e.g. "L2-Top-Back". */
  position?: string;
  /** A caveat that belongs on this specific antenna, not the box. */
  note?: string;
  /** Reader port with no antenna on it. Drawn, because a gap in the port list
   *  is otherwise indistinguishable from an antenna that read nothing. */
  unwired?: boolean;
}

export interface IsoBox {
  antennas: PlacedAntenna[];
  /** Ports with no antenna wired to them, if the layout records that. */
  unwired: number[];
  edges: { x1: number; y1: number; x2: number; y2: number; faint?: boolean }[];
  width: number;
  height: number;
  doorLabel: { x: number; y: number };
}

/** Each bay top-to-bottom, in drawing order from the door end. */
const RIGHT_BAYS: number[][] = [
  [5, 4, 3, 2],
  [8, 9, 10, 11],
  [15, 14, 13, 12],
];
const LEFT_BAYS: number[][] = [
  [18, 19, 20, 21],
  [25, 24, 23, 22],
  [28, 29, 30, 31],
];
/** The flat plate belonging to each bay. The reference script's `vgroups` pair
 *  them exactly this way: one bay plus its plate is one group of five. */
const RIGHT_PLATES = [6, 7, 16];
const LEFT_PLATES = [17, 26, 27];
/** Ceiling plates in the centre channel. */
const CENTRE_DOOR = 1;
const CENTRE_FAR = 32;

const SHELF_LEVELS = 4;
const PLATE_LEVEL = SHELF_LEVELS; // drawn above the top shelf

const ISO_COS = Math.cos(Math.PI / 6); // 30°
const ISO_SIN = Math.sin(Math.PI / 6);

/**
 * Lay the box out isometrically: two walls of three bays, four shelves high,
 * the plates above them, and the two centre plates on the ceiling.
 *
 * Spacing is tuned against the collision test in `__tests__`, not by eye -- an
 * earlier version reused the along-box scale for the across-box axis and two
 * antennas on opposite walls landed 4px apart with a 7px radius.
 */
export function isoBox32(_key: LayoutKey = "reference-32"): IsoBox {
  const bayW = 52; // along the length of the box, bay to bay
  const sideW = 104; // across it -- its own scale, which is what separates the walls
  const levelH = 18;
  const padX = 136;
  const padY = 112;

  const project = (slot: number, side: number, level: number): { x: number; y: number } => ({
    x: padX + slot * bayW * ISO_COS - side * sideW * ISO_COS,
    y: padY + slot * bayW * ISO_SIN + side * sideW * ISO_SIN - level * levelH,
  });

  const antennas: PlacedAntenna[] = [];
  const push = (
    port: number, surface: Surface, slot: number, side: number, level: number,
  ): void => {
    antennas.push({ port, surface, slot, level, ...project(slot, side, level) });
  };

  RIGHT_BAYS.forEach((bay, bayIndex) => {
    // bay[0] is the TOP shelf, so index 0 -> level 3 and index 3 -> level 0.
    bay.forEach((port, i) => push(port, "right", bayIndex, 0, SHELF_LEVELS - 1 - i));
    push(RIGHT_PLATES[bayIndex], "right", bayIndex + 0.42, 0, PLATE_LEVEL);
  });
  LEFT_BAYS.forEach((bay, bayIndex) => {
    bay.forEach((port, i) => push(port, "left", bayIndex, 1, SHELF_LEVELS - 1 - i));
    push(LEFT_PLATES[bayIndex], "left", bayIndex + 0.42, 1, PLATE_LEVEL);
  });
  push(CENTRE_DOOR, "ceiling", -0.75, 0.5, PLATE_LEVEL);
  push(CENTRE_FAR, "ceiling", RIGHT_BAYS.length - 0.25, 0.5, PLATE_LEVEL);

  // Container outline: floor and ceiling rectangles plus the verticals.
  const lo = -1.2;
  const hi = RIGHT_BAYS.length + 0.1;
  const corners = [
    project(lo, 0, -0.8), project(hi, 0, -0.8),
    project(hi, 1, -0.8), project(lo, 1, -0.8),
  ];
  const tops = [
    project(lo, 0, PLATE_LEVEL + 0.7), project(hi, 0, PLATE_LEVEL + 0.7),
    project(hi, 1, PLATE_LEVEL + 0.7), project(lo, 1, PLATE_LEVEL + 0.7),
  ];
  const edges: IsoBox["edges"] = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    const ta = tops[i];
    const tb = tops[(i + 1) % 4];
    edges.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, faint: true });
    edges.push({ x1: ta.x, y1: ta.y, x2: tb.x, y2: tb.y, faint: true });
    edges.push({ x1: a.x, y1: a.y, x2: ta.x, y2: ta.y, faint: true });
  }

  const xs = [...antennas.map((a) => a.x), ...corners.map((c) => c.x), ...tops.map((t) => t.x)];
  const ys = [...antennas.map((a) => a.y), ...corners.map((c) => c.y), ...tops.map((t) => t.y)];
  return {
    antennas,
    edges,
    unwired: [],
    width: Math.max(...xs) + 40,
    height: Math.max(...ys) + 26,
    doorLabel: project(lo - 0.45, 0.5, 1.8),
  };
}

/* ------------------------------------------------------------------------ *
 * TXAM SB093 -- a surveyed box, not a drawn one.
 * ------------------------------------------------------------------------ */

/**
 * SB093 (`SCU-PI9KT2PZ-GEN5` / `gen5-rpi-9kt2pz`, TXAM Pumps) is NOT the
 * reference 32-port box, and drawing it as one puts every antenna in the wrong
 * place. Source: `docs/txam-antenna-mapping/` -- 26 photos of the antennas
 * taken 2026-05-05/06, a tech visit on 2026-05-11, and `layout_reference.html`,
 * regenerated from the box's own `antenna LEFT JOIN antenna_position` rows.
 *
 * How it differs, and why none of it could be guessed:
 *
 *  - **28 antennas on a 32-port reader.** Ports 11, 15, 17 and 21 have no coax
 *    on them. A flat 1..32 grid invents four antennas that do not exist.
 *  - **10 ceiling + 18 shelf**, not three bays per wall plus plates. The left
 *    wall carries TWO racks (L1 near the door, L2 behind it) and twelve
 *    antennas between them; the right wall carries one rack of six; the
 *    remaining ten ring the ceiling.
 *  - **Antenna 1 is at the CARGO end**, on the L2 rack -- the opposite end from
 *    the reference box, where port 1 is the ceiling plate at the door. So the
 *    door marker here is placed from surveyed coordinates and not, as on
 *    `reference-32`, by knowing where port 1 is.
 *  - **Every antenna carries a Rebound reference sticker** whose EPC is known.
 *    Nothing else in the fleet has this, and it is what let the survey prove
 *    the cabling rather than trust the labels.
 *
 * THE LABELS USED TO LIE, AND THE STICKERS ARE WHY WE KNOW. Of 24 antennas
 * photographed with both a printed `1-NN` label and a sticker, only 12 agreed
 * with the port that actually read that sticker loudest -- several off by 3-11
 * positions with 15-30 dBm of margin, which is a coax shuffle, not noise. On
 * 2026-05-11 the tech relabelled the whole rig so every panel now reads `1-N`
 * for its true reader port N. The coordinates below are the corrected,
 * post-relabel state.
 *
 * Interior 19 ft (door -> cargo) x 7.5 ft (left wall -> right wall) x 7.9 ft.
 * Coordinates are feet exactly as surveyed, so a correction upstream is a
 * number change here and nothing else.
 */
interface SurveyedAntenna {
  port: number;
  label: string;
  sticker: string | null;
  position: string;
  surface: Surface;
  /** Feet along the box from the door wall. */
  fx: number;
  /** Feet across the box from the left wall. */
  fy: number;
  /** Feet off the floor. */
  fz: number;
  note?: string;
}

const TXAM_SB093_ANTENNAS: SurveyedAntenna[] = [
  // Left wall -- rack L1 (door end) and rack L2 (cargo end), three levels each.
  { port: 1, label: "1-1", sticker: "DC2303130480", position: "L2-Top-Back", surface: "left", fx: 15.25, fy: 0.0, fz: 5.5 },
  { port: 2, label: "1-2", sticker: "DC2303130479", position: "L2-Top-Front", surface: "left", fx: 11.75, fy: 0.0, fz: 5.5 },
  { port: 3, label: "1-3", sticker: "DC2303130508", position: "L1-Mid-Front", surface: "left", fx: 4.75, fy: 0.0, fz: 3.5 },
  { port: 4, label: "1-4", sticker: "DC2303130484", position: "L1-Mid-Back", surface: "left", fx: 8.25, fy: 0.0, fz: 3.5 },
  { port: 5, label: "1-5", sticker: "DC2303130482", position: "L2-Bot-Back", surface: "left", fx: 15.25, fy: 0.0, fz: 1.5 },
  { port: 6, label: "1-6", sticker: "DC2303130483", position: "L2-Bot-Front", surface: "left", fx: 11.75, fy: 0.0, fz: 1.5 },
  { port: 7, label: "1-7", sticker: "DC2303130507", position: "L2-Mid-Back", surface: "left", fx: 15.25, fy: 0.0, fz: 3.5 },
  { port: 8, label: "1-8", sticker: "DC2303130486", position: "L1-Bot-Back", surface: "left", fx: 8.25, fy: 0.0, fz: 1.5 },
  {
    port: 9, label: "1-9", sticker: "DC2303130485", position: "L1-Bot-Front",
    surface: "left", fx: 4.75, fy: 0.0, fz: 1.5,
    note:
      "Silent since the 2026-05-11 visit -- the connector was probably bumped "
      + "loose while the adjacent ports were worked. Antenna and sticker are "
      + "intact (485 still reads at -47 dBm from port 14, across the box). "
      + "Site task: reseat the connector at reader port 9.",
  },
  { port: 10, label: "1-10", sticker: "DC2303130490", position: "L1-Top-Back", surface: "left", fx: 8.25, fy: 0.0, fz: 5.5 },
  { port: 12, label: "1-12", sticker: "DC2303130491", position: "L1-Top-Front", surface: "left", fx: 4.75, fy: 0.0, fz: 5.5 },
  {
    port: 14, label: "1-14", sticker: "DC2303130487", position: "L2-Mid-Front",
    surface: "left", fx: 11.75, fy: 0.0, fz: 3.5,
    note:
      "Rerouted from reader port 11 to port 14 on 2026-05-11 and relabelled to "
      + "match. Sticker 487 is a DEAD CHIP -- the antenna works but its own face "
      + "sticker never reads at any power. Replace it on the next visit and "
      + "record the new EPC.",
  },
  // Right wall -- one rack of six.
  { port: 13, label: "1-13", sticker: "DC2303130493", position: "R-Mid-Front", surface: "right", fx: 9.5, fy: 7.5, fz: 3.5 },
  { port: 19, label: "1-19", sticker: "DC2303130496", position: "R-Top-Front", surface: "right", fx: 9.5, fy: 7.5, fz: 5.5 },
  { port: 20, label: "1-20", sticker: "DC2303130492", position: "R-Bot-Back", surface: "right", fx: 14.5, fy: 7.5, fz: 1.5 },
  { port: 22, label: "1-22", sticker: "DC2303130494", position: "R-Bot-Front", surface: "right", fx: 9.5, fy: 7.5, fz: 1.5 },
  {
    port: 23, label: "1-23", sticker: "DC2303130488", position: "R-Mid-Back",
    surface: "right", fx: 14.5, fy: 7.5, fz: 3.5,
    note:
      "Sticker corrected to 488 by the tech on 2026-05-11 (our DB had said 495, "
      + "inferred from scan data). Production scans still read 495 loudest here, "
      + "so the POSITION name may need re-verifying on the next visit.",
  },
  { port: 24, label: "1-24", sticker: "DC2303130497", position: "R-Top-Back", surface: "right", fx: 14.5, fy: 7.5, fz: 5.5 },
  // Ceiling -- ten, ringing the perimeter.
  {
    port: 16, label: "1-16", sticker: null, position: "Ceil-FR-Corner",
    surface: "ceiling", fx: 1.25, fy: 6.8, fz: 7.9,
    note:
      "Relabelled 2026-05-08 from unlabelled. Which sticker is on its face is "
      + "UNKNOWN -- 488 went to port 23, and what port 16 reads of 488 is "
      + "cross-box pickup at -56 dBm, not a face read. Record on the next visit.",
  },
  { port: 18, label: "1-18", sticker: "DC2303130509", position: "Ceil-RW-Mid-F", surface: "ceiling", fx: 6.5, fy: 6.8, fz: 7.9 },
  { port: 25, label: "1-25", sticker: "DC2303130505", position: "Ceil-Door", surface: "ceiling", fx: 1.5, fy: 3.75, fz: 7.9 },
  { port: 26, label: "1-26", sticker: "DC2303130478", position: "Ceil-LW-Front", surface: "ceiling", fx: 5.0, fy: 0.7, fz: 7.9 },
  { port: 27, label: "1-27", sticker: "DC2303130481", position: "Ceil-LW-Mid", surface: "ceiling", fx: 10.0, fy: 0.7, fz: 7.9 },
  { port: 28, label: "1-28", sticker: "DC2303130489", position: "Ceil-LW-Back", surface: "ceiling", fx: 16.0, fy: 0.7, fz: 7.9 },
  { port: 29, label: "1-29", sticker: "DC2303130506", position: "Ceil-Cargo", surface: "ceiling", fx: 18.3, fy: 3.75, fz: 7.9 },
  { port: 30, label: "1-30", sticker: "DC2303130499", position: "Ceil-RW-Mid-B", surface: "ceiling", fx: 11.0, fy: 6.8, fz: 7.9 },
  { port: 31, label: "1-31", sticker: "DC2303130498", position: "Ceil-RW-Back", surface: "ceiling", fx: 14.0, fy: 6.8, fz: 7.9 },
  { port: 32, label: "1-32", sticker: "DC2303130510", position: "Ceil-RW-Front", surface: "ceiling", fx: 3.75, fy: 6.8, fz: 7.9 },
];

/** Reader ports with no coax on them. Drawn hollow, off to the side, so a gap
 *  in a port list reads as "nothing is wired here" rather than "this read
 *  nothing" -- which are different facts and only one is a fault. */
const TXAM_SB093_UNWIRED = [11, 15, 17, 21];

/** Interior, in feet, from the survey. */
const TXAM_BOX_FT = { length: 19, width: 7.5, height: 7.9 };

/**
 * SB093 drawn from its surveyed coordinates.
 *
 * Same isometric idiom as `isoBox32`, but the input is feet rather than bay and
 * level indices, so the picture is the measurement rather than a reading of a
 * drawing. The across-box axis keeps its own scale for the same reason it does
 * there -- at a shared scale the two walls do not clear each other.
 */
export function isoBoxTxamSb093(): IsoBox {
  // Three independent scales, and the vertical one is the reason this box gets
  // its own: at anything near the along-box scale the ceiling ring lands on top
  // of the rack tops. Ports 14 (L2-Mid-Front) and 29 (Ceil-Cargo) are the pair
  // that proves it -- their separation in feet points almost exactly down the
  // projection's blind axis, so they collide at EVERY uniform scale, and only a
  // tall vertical exaggeration pulls them apart. The collision test in
  // `__tests__/antennaGeometrySb093.test.ts` is what found that.
  const alongPxPerFt = 8;
  const acrossPxPerFt = 16;
  const upPxPerFt = 16.5;
  const padX = 140;
  const padY = 120;

  const project = (fx: number, fy: number, fz: number): { x: number; y: number } => ({
    x: padX + fx * alongPxPerFt * ISO_COS - fy * acrossPxPerFt * ISO_COS,
    y: padY + fx * alongPxPerFt * ISO_SIN + fy * acrossPxPerFt * ISO_SIN - fz * upPxPerFt,
  });

  const antennas: PlacedAntenna[] = TXAM_SB093_ANTENNAS.map((a) => ({
    port: a.port,
    surface: a.surface,
    slot: a.fx,
    level: a.fz,
    label: a.label,
    sticker: a.sticker,
    position: a.position,
    note: a.note,
    ...project(a.fx, a.fy, a.fz),
  }));

  // The unwired ports are real reader ports with no place in the box, so they
  // are parked in a row outside the door wall rather than given a position
  // they do not have.
  TXAM_SB093_UNWIRED.forEach((port, i) => {
    antennas.push({
      port,
      surface: "ceiling",
      slot: -3.4,
      level: 0,
      unwired: true,
      note: "No antenna is wired to this reader port.",
      ...project(-3.4, 0.9 + i * 2.1, 1.4),
    });
  });

  const { length: L, width: W, height: H } = TXAM_BOX_FT;
  const floor = [project(0, 0, 0), project(L, 0, 0), project(L, W, 0), project(0, W, 0)];
  const roof = [project(0, 0, H), project(L, 0, H), project(L, W, H), project(0, W, H)];
  const edges: IsoBox["edges"] = [];
  for (let i = 0; i < 4; i++) {
    const a = floor[i];
    const b = floor[(i + 1) % 4];
    const ta = roof[i];
    const tb = roof[(i + 1) % 4];
    edges.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, faint: true });
    edges.push({ x1: ta.x, y1: ta.y, x2: tb.x, y2: tb.y, faint: true });
    edges.push({ x1: a.x, y1: a.y, x2: ta.x, y2: ta.y, faint: true });
  }

  const xs = [...antennas.map((a) => a.x), ...floor.map((c) => c.x), ...roof.map((t) => t.x)];
  const ys = [...antennas.map((a) => a.y), ...floor.map((c) => c.y), ...roof.map((t) => t.y)];
  return {
    antennas,
    edges,
    unwired: [...TXAM_SB093_UNWIRED],
    width: Math.max(...xs) + 40,
    height: Math.max(...ys) + 26,
    // The door is the low-x wall. Port 1 is at the CARGO end on this box, so
    // this cannot be derived from port 1 the way reference-32 does it.
    doorLabel: project(-1.9, W / 2, 2.2),
  };
}

/** The drawing for a layout key. Anything un-surveyed is the reference box,
 *  which is what every other 32-port box in the fleet is. */
export function isoBoxFor(key: LayoutKey): IsoBox {
  return key === "txam-sb093" ? isoBoxTxamSb093() : isoBox32(key);
}

export interface BoxDrawing {
  /** null when this reader cannot be drawn as a box. */
  key: LayoutKey | null;
  label: string;
}

/**
 * Which drawing to use for a reader.
 *
 * A 32-port reader is a 20 ft box and its layout is confirmed against the
 * engineering drawing, so it draws -- the recorded shelf type is provenance in
 * the label, not a gate. Port count is a weak signal either way: a box whose
 * top port read nothing this scan reports 31 and is still the same box. Only
 * something LARGER than 32 ports (the 3-reader 40 ft box) is a shape we have
 * no drawing for.
 */
export function boxDrawingFor(
  layoutKey: string | null | undefined,
  portCount: number,
  /** How many readers the box has. A box with more than one is a different
   *  physical shape, not N copies of this one — see below. */
  readerCount = 1,
): BoxDrawing {
  // *** A MULTI-READER BOX IS NOT N COPIES OF THE 20 FT BOX. ***
  // The reference drawing is one 32-port reader in a 20 ft container. Called
  // per reader, the port-count test below passes for each of them separately,
  // so a two-reader box drew the 20 ft box TWICE — asserting a shape nobody
  // has checked, twice as confidently. Measured 2026-09-18: SB023
  // (PIRX89UP) runs two 32-port readers and SB100 runs three (95 antennas).
  // Neither has a drawing, and inventing one is worse than saying so.
  if (readerCount > 1) {
    return {
      key: null,
      label: `${readerCount} readers · no drawing for this shape`,
    };
  }
  // A SURVEYED layout wins outright, before any port-count reasoning. SB093
  // has 28 antennas on a 32-port reader and typically reports low twenties in
  // a scan, so every heuristic below would mis-describe it -- and unlike the
  // reference box, its layout is measured rather than inferred.
  if (layoutKey === "txam-sb093") {
    return {
      key: "txam-sb093",
      label: "20 ft box · 28 antennas on 32 ports · surveyed layout",
    };
  }
  if (portCount > 32) {
    return { key: null, label: `${portCount} ports · no drawing for this shape` };
  }
  const known = layoutKey && (KNOWN_LAYOUTS as readonly string[]).includes(layoutKey);
  return {
    key: "reference-32",
    label: known
      ? `20 ft box · 32 ports · ${layoutKey} shelving`
      : "20 ft box · 32 ports",
  };
}

/** Decode an MSB-first antenna mask ("20000000" -> [3]). Antenna 1 is the top
 *  bit. The server already sends the decoded list; this exists for anywhere a
 *  raw mask turns up (a scan payload, a paste from the reader). */
export function maskToAntennas(maskHex: string | null | undefined): number[] {
  if (!maskHex) return [];
  const mask = Number.parseInt(maskHex, 16);
  if (!Number.isFinite(mask)) return [];
  const out: number[] = [];
  for (let port = 1; port <= 32; port++) {
    // eslint-disable-next-line no-bitwise
    if ((mask >>> (32 - port)) & 1) out.push(port);
  }
  return out;
}
